/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NavigationPrompt from 'react-router-navigation-prompt';

import confirmNavigationSrc from 'assets/icons/confirmNavigation.svg';
import { DeclarationDialog } from 'components/DeclarationForm/dialogs/Dialog';
import { IQuizFormValues } from '../quiz.types';

// Styled icon — reuse same style as the Formik-based dialog
import { StyledIcon } from 'components/DeclarationForm/dialogs/NavigationCheckDialog/styles';

/**
 * Mirrors `NavigationCheckDialog` from the DeclarationForm, but uses
 * `react-hook-form`'s `formState.isDirty` instead of Formik's `dirty`.
 *
 * Also installs a `beforeunload` handler so the browser's native "changes
 * may not be saved" dialog fires on page reload / tab close.
 */
export function QuizNavigationCheckDialog(): JSX.Element {
  const { t } = useTranslation();
  const {
    formState: { isDirty },
  } = useFormContext<IQuizFormValues>();

  // Native browser dialog on reload / tab close
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): string => {
      if (!isDirty) return '';
      e.preventDefault();
      // Modern browsers ignore the custom message but still show the dialog.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return (
    <NavigationPrompt when={isDirty}>
      {({ onConfirm, onCancel }) => (
        <DeclarationDialog
          isOpen
          isClosable
          adornment={<StyledIcon src={confirmNavigationSrc} alt="icon" />}
          onCancel={onCancel}
          onConfirm={onConfirm}
          title={t('challenges.declaration.confirmNavigation.title')}
          explanation={t(
            'challenges.declaration.confirmNavigation.explanation',
          )}
          cancelLabel={t('challenges.declaration.confirmNavigation.cancel')}
          confirmLabel={t('challenges.declaration.confirmNavigation.confirm')}
        />
      )}
    </NavigationPrompt>
  );
}
